import { SiteUrls } from '@tableau/shared-urls';
import { Tooltip } from '@tableau/widgets';
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { SvgWarning } from '../../../../../gen-src/icons';
import {
  CenteredFadeInSpinner,
  HorizontalFlexContainer,
  VerticalFlexContainer,
} from '../../../data-catalog-shared';
import { LineageItemConfig, lineageItemKey, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { CartoucheConnectorLine } from '../style/LineageCartoucheStyles';
import { FilterIndicatorWithClearButton } from './FilterIndicatorWithClearButton';
import { LineageAnchor, LineageAnchorProps } from './LineageAnchor';
import { LineageCartouche } from './LineageCartouche';
import { LineageItemProps } from './LineageItemProps';
import { LineageItemType } from './LineageItemType';
import { lineageHeaderClass, LineageNavContainer, lineageNavCssClass, LineageNavHeader } from './LineageNavStyles';
import { SingleLineageItem } from './SingleLineageItem';

export type Config<TCounts> = ReadonlyArray<LineageSection<TCounts, unknown>>;

export interface LineageNavProps<TCounts> {
  readonly itemId: string;
  readonly anchorName: string | null;
  readonly anchorCount: number | null;
  readonly countsLoading: boolean;
  readonly unfilteredCounts: TCounts | null;
  readonly filteredCounts: TCounts | null;
  readonly urlSearchValue: string;
  readonly filteredItemName: string | null;
  readonly clearFilters: () => void;
  readonly urls: SiteUrls;
  readonly activeLineageItemKey: string;
  readonly lineageConfiguration: Config<TCounts>;
  readonly filteredOnItemType: LineageItemType | null;
  readonly shouldShowFlowParametersWarning?: boolean;
}

export class LineageNav<TCounts> extends React.PureComponent<LineageNavProps<TCounts>> {

  private hasInitialLoadCompleted() {
    return !!this.props.unfilteredCounts;
  }

  public override render(): React.ReactNode {
    const contents = this.hasInitialLoadCompleted() ? (
      <>
        {this.renderHeader()}
        {this.renderNavItems()}
      </>
    ) : this.renderLoadingIndicator();

    return (
      <LineageNavContainer>
        <VerticalFlexContainer fillFlexParent={true} additionalClassNames={[lineageNavCssClass]}>
          {contents}
        </VerticalFlexContainer>
      </LineageNavContainer>
    );
  }

  private renderLoadingIndicator(): React.ReactNode {
    return <CenteredFadeInSpinner />;
  }

  private renderHeader(): React.ReactNode {
    return (
      <LineageNavHeader>
        <HorizontalFlexContainer additionalClassNames={[lineageHeaderClass]}>
          <span>
            {this.props.shouldShowFlowParametersWarning &&
              <Tooltip
                getContent={() => <div>{Messages.FlowParametersLineageWarning()}</div>}
              ><SvgWarning style={{ marginRight: 8 }} /></Tooltip>
            }
            {Messages.LineageNavHeader()}
          </span>
          {this.props.filteredItemName ?
            <FilterIndicatorWithClearButton
              clearFilters={this.props.clearFilters}
              filteredItemName={this.props.filteredItemName}
            />
            : null
          }
        </HorizontalFlexContainer>
      </LineageNavHeader>
    );
  }

  private renderNavItems(): React.ReactNode {
    const sectionsToRender = this.getItemsWithNonzeroCounts();
    const renderedSections = sectionsToRender.map(
      section => this.rendererForLineageSectionType[section.sectionType](section)
    );

    const sectionsWithConnectors: React.ReactNode[] = [];
    renderedSections.forEach((section, i) => {
      if (i !== 0) {
        sectionsWithConnectors.push(<CartoucheConnectorLine key={i} />);
      }
      sectionsWithConnectors.push(section);
    });
    return sectionsWithConnectors;
  }

  private getItemsWithNonzeroCounts(): Config<TCounts> {
    const configSections = this.props.lineageConfiguration;

    const sectionsWithFilteredItems = configSections.map(section => {
      // Certain section types should never be removed
      if (section.sectionType === LineageSectionType.Anchor || section.sectionType === LineageSectionType.Owners) {
        return section;
      }

      const filteredItems = section.items.filter(item => this.props.unfilteredCounts && item.count(this.props.unfilteredCounts));
      return { ...section, items: filteredItems };
    });

    const filteredSections = sectionsWithFilteredItems.filter(section => section.items.length > 0);
    return filteredSections;
  }

  private rendererForLineageSectionType: Record<LineageSectionType, (section: LineageSection<TCounts, unknown>) => React.ReactNode> = {
    /* eslint-disable @typescript-eslint/no-unsafe-assignment */
    [LineageSectionType.Group]: this.renderCartouche.bind(this),
    [LineageSectionType.Anchor]: this.renderAnchor.bind(this),
    [LineageSectionType.Owners]: this.renderSingleItem.bind(this),
    /* eslint-enable @typescript-eslint/no-unsafe-assignment */
  };

  private renderCartouche(section: LineageSection<TCounts, unknown>): React.ReactNode {
    const lineageItems = section.items.map(item => this.getLineageItemProps(item, section));
    return <LineageCartouche lineageItems={lineageItems} key={section.key} />;
  }

  private renderAnchor(section: LineageSection<TCounts, unknown>): React.ReactNode {
    const item = section.items[0]; // Anchor sections should have only one item
    return <LineageAnchor {...this.getAnchorProps(item, section)} />;
  }

  private renderSingleItem(section: LineageSection<TCounts, unknown>): React.ReactNode {
    const item = section.items[0]; // Single-item sections should have only one item
    return <SingleLineageItem {...this.getLineageItemProps(item, section)} />;
  }

  private getLineageItemProps(
    item: LineageItemConfig<TCounts, unknown>,
    section: LineageSection<TCounts, unknown>,
  ): LineageItemProps & { key: string } {
    const unfilteredCount = this.props.unfilteredCounts && item.count(this.props.unfilteredCounts);
    const filteredCount = this.props.filteredCounts && item.count(this.props.filteredCounts);

    return {
      key: item.lineageItemType,
      label: item.messagesForLineage.withoutCount(),
      countLoading: !!this.props.filteredItemName && this.props.countsLoading,
      filteredCount,
      unfilteredCount,
      lineageItemType: item.lineageItemType,
      isCurrentPane: lineageItemKey({ lineageItem: item, lineageSection: section }) === this.props.activeLineageItemKey,
      linkHref: item.path(this.props.urls, this.props.itemId) + this.props.urlSearchValue,
      buttonAltText: item.messagesForLineage.withoutCount(),
    };
  }

  private getAnchorProps(
    item: LineageItemConfig<TCounts, unknown>,
    section: LineageSection<TCounts, unknown>,
  ): LineageAnchorProps & { key: string } {
    return {
      key: item.lineageItemType,
      label: item.messagesForLineage.withoutCount(),
      lineageItemType: item.lineageItemType,
      isCurrentPane: lineageItemKey({ lineageItem: item, lineageSection: section }) === this.props.activeLineageItemKey,
      linkHref: item.path(this.props.urls, this.props.itemId) + this.props.urlSearchValue,
      buttonAltText: item.messagesForLineage.withoutCount(),
      isFilteredOnThisType: item.lineageItemType === this.props.filteredOnItemType,
      anchorName: this.props.anchorName,
      selectedItemCount: this.props.filteredItemName ? 1 : 0,
      anchorCount: this.props.anchorCount,
    };
  }
}
